<app-spinner *ngIf="isSpinner" [isSpinning]="isSpinner"></app-spinner>
<div class="dashboard-container" *ngIf="!isSpinner">
  <nz-card class="card-body">
    <div class="dashboard-wrapper">
      <h2 class="dashboard-title">{{'dashboard' |translate}}</h2>

      <!-- Two tabs: Paper A (basic sciences) and Paper B (the rest) -->
      <div class="paper-tabs" role="tablist" [attr.aria-label]="'ovPapersLabel' |translate">
        <button *ngFor="let paper of papers; trackBy: trackByPaper" type="button" role="tab" class="paper-tab"
          [id]="'paper-tab-' + paper.key" [attr.aria-controls]="'paper-panel-' + paper.key"
          [attr.aria-selected]="paper.key === activePaper" [attr.tabindex]="paper.key === activePaper ? 0 : -1"
          [class.active]="paper.key === activePaper" (click)="selectPaper(paper.key)" (keydown)="onTabKeydown($event)">
          <span class="paper-tab-name">{{paper.labelKey |translate}}</span>
          <span class="paper-tab-sub">{{paper.subtitleKey |translate}}</span>
        </button>
      </div>

      <div *ngFor="let paper of papers; trackBy: trackByPaper" class="paper-panel" role="tabpanel" tabindex="0"
        [id]="'paper-panel-' + paper.key" [attr.aria-labelledby]="'paper-tab-' + paper.key"
        [hidden]="paper.key !== activePaper">

        <!-- Summary card: learning status of the paper -->
        <section class="summary-card" [attr.aria-labelledby]="'summary-title-' + paper.key">
          <h3 class="summary-title" [id]="'summary-title-' + paper.key">
            {{'ovLearningStatus' |translate}} · {{paper.labelKey |translate}}
          </h3>
          <div class="summary-main">
            <div class="summary-figure">
              <span class="summary-percent">{{paper.summary.percent}}</span>
              <span class="summary-percent-label">{{'ovHitRate' |translate}}</span>
            </div>
            <div class="summary-progress">
              <p class="summary-answered">{{fmt('ovAnsweredOf', {answered: paper.summary.answered, total: paper.summary.total})}}</p>
              <div class="stack-bar stack-bar-large" role="img"
                [attr.aria-label]="barLabel(paper.summary.correct, paper.summary.wrong, paper.summary.blank)">
                <span class="seg seg-correct" [style.width.%]="paper.bar.correct"></span>
                <span class="seg seg-wrong" [style.width.%]="paper.bar.wrong"></span>
                <span class="seg seg-open" [style.width.%]="paper.bar.open"></span>
              </div>
              <ul class="stat-legend">
                <li><i class="swatch swatch-correct" aria-hidden="true"></i>{{'correct' |translate}} <b>{{paper.summary.correct}}</b></li>
                <li><i class="swatch swatch-wrong" aria-hidden="true"></i>{{'wrong' |translate}} <b>{{paper.summary.wrong}}</b></li>
                <li><i class="swatch swatch-open" aria-hidden="true"></i>{{'unanswered' |translate}} <b>{{paper.summary.blank}}</b></li>
              </ul>
            </div>
          </div>

          <div class="weakest">
            <h4 class="weakest-title">{{'ovWeakestChapters' |translate}}</h4>
            <ol class="weakest-list" *ngIf="paper.weakest.length > 0">
              <li *ngFor="let row of paper.weakest" class="weakest-item">
                <span class="weakest-name">{{row.name}}</span>
                <span class="weakest-value">{{percentCorrect(row)}}</span>
                <span class="light-chip" [ngClass]="'light-' + light(row)">
                  <span class="light-symbol" aria-hidden="true">{{lightSymbol(light(row))}}</span>{{lightKey(light(row)) |translate}}
                </span>
              </li>
            </ol>
            <p class="weakest-empty" *ngIf="paper.weakest.length === 0">{{fmt('ovWeakestNone', {n: minAnswered})}}</p>
          </div>
        </section>

        <!-- Sorting -->
        <div class="sort-bar" role="group" [attr.aria-label]="'sortBy' |translate">
          <span class="sort-label" aria-hidden="true">{{'sortBy' |translate}}</span>
          <button type="button" class="sort-button" (click)="setSort('default')"
            [class.active]="sortMode === 'default'" [attr.aria-pressed]="sortMode === 'default'">
            {{'sortChapterOrder' |translate}}
          </button>
          <button type="button" class="sort-button" (click)="setSort('weakness')"
            [class.active]="sortMode === 'weakness'" [attr.aria-pressed]="sortMode === 'weakness'"
            [attr.aria-label]="'sortWeakestFirst' |translate">
            {{'ovSortWeakestShort' |translate}}
          </button>
        </div>

        <!-- Chapter cards -->
        <ul class="chapter-cards" [attr.aria-label]="'capital' |translate">
          <li *ngFor="let row of paper.rows; trackBy: trackById" class="chapter-card" [ngClass]="'card-' + light(row)">
            <div class="chapter-head">
              <h4 class="chapter-name"><span class="chapter-number">{{chapterNumber(row)}}.</span> {{row.name}}</h4>
              <span class="light-chip" [ngClass]="'light-' + light(row)">
                <span class="light-symbol" aria-hidden="true">{{lightSymbol(light(row))}}</span>{{lightKey(light(row)) |translate}}
              </span>
            </div>
            <div class="chapter-score">
              <span class="chapter-percent">{{percentCorrect(row)}}</span>
              <span class="chapter-percent-label" *ngIf="light(row) !== 'none'">{{'ovCorrectShare' |translate}}</span>
              <span class="chapter-hint" *ngIf="light(row) === 'none'">{{fmt('ovFewAnswers', {n: answered(row), min: minAnswered})}}</span>
            </div>
            <div class="stack-bar" role="img"
              [attr.aria-label]="barLabel(row.correct_question_count, row.wrong_question_count, row.blank_question_count)">
              <span class="seg seg-correct" [style.width.%]="segments(row).correct"></span>
              <span class="seg seg-wrong" [style.width.%]="segments(row).wrong"></span>
              <span class="seg seg-open" [style.width.%]="segments(row).open"></span>
            </div>
            <ul class="stat-legend stat-legend-compact">
              <li><i class="swatch swatch-correct" aria-hidden="true"></i>{{'correct' |translate}} <b>{{row.correct_question_count}}</b></li>
              <li><i class="swatch swatch-wrong" aria-hidden="true"></i>{{'wrong' |translate}} <b>{{row.wrong_question_count}}</b></li>
              <li><i class="swatch swatch-open" aria-hidden="true"></i>{{'unanswered' |translate}} <b>{{row.blank_question_count}}</b></li>
            </ul>
            <div class="chapter-foot">
              <span class="chapter-total">{{fmt('ovQuestionsCount', {n: row.total_question})}}</span>
              <button type="button" class="practice-link" (click)="practiceChapter(row)"
                [attr.aria-label]="fmt('ovPracticeChapter', {name: row.name})">{{'ovPractice' |translate}}</button>
            </div>
          </li>
        </ul>
      </div>

      <p class="traffic-legend">{{fmt('ovLegend', {n: minAnswered})}}</p>

      <p class="overall-line">
        <b>{{'total' |translate}}:</b>
        {{fmt('ovAnsweredOf', {answered: total.correct_question_count + total.wrong_question_count, total: total.total_question})}}
        · {{percentCorrect(total)}} {{'ovCorrectShare' |translate}}
      </p>

      <div class="button-group">
        <div>
          <button (click)="practiceWrong()" nz-button nzType="primary" nzSize="large" class="first-button">{{'practiceFailed' |translate}}</button>
          <button (click)="practiceBlank()" nz-button nzType="primary" nzSize="large">{{'practiceUnanswered' |translate}}</button>
        </div>
      </div>
      <div class="reset-questions">
        <a (click)="openModel()">{{'resetQuestions' |translate}}</a>
      </div>
    </div>
  </nz-card>
</div>
